import React, { useState, useEffect } from 'react';
import { FiPhone, FiMail, FiMapPin, FiInstagram, FiFacebook, FiTwitter, FiYoutube, FiLinkedin, FiArrowRight } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useData } from '../contexts/DataContext';

const Footer = () => {
  // Get data from global context
  const { settings } = useData();

  // Debug: Log all settings data
  useEffect(() => {
    if (settings && Object.keys(settings).length > 0) {
      // Console logs removed - data is working correctly
    }
  }, [settings]);

  const therapyLinks = [
    'Acupuncture Therapy',
    'Cupping Massage',
    'Hammer Therapy',
    'Naturopathy',
    'Magnetic Therapy',
    'Potali Therapy'
  ];

  const quickLinks = [
    { name: 'About Us', href: '/about' },
    { name: 'Courses', href: '/courses' },
    { name: 'Gallery', href: '/gallery' },
    { name: 'FAQ', href: '/faq' },
    { name: 'Terms of Service', href: '/terms' },
    { name: 'Privacy Policy', href: '/privacy' },
    { name: 'Cookie Policy', href: '/cookie-policy' },
    { name: 'Contact us', href: '/contact' }
  ];

  return (
    <footer className="bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-700 text-white pt-20 pb-28 lg:pb-10 overflow-hidden relative">
      {/* Decorative background element */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500 via-emerald-300 to-emerald-500"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-16">
          {/* Brand Column */}
          <div className="space-y-6">
            <div className="space-y-2">
              <img 
                src={settings.logo_light || "/backend/uploads/logo_dark_c2bee98ff4f67774.png"} 
                alt="Jiva Nadi" 
                className="h-16 w-auto object-contain bg-white rounded-lg p-2 backdrop-blur-sm"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div className="hidden">
                <h2 className="text-2xl font-bold tracking-tight">JIVA NADI</h2>
                <p className="text-emerald-400 text-xs font-medium uppercase tracking-widest">Therapy & Chiropractor Center</p>
              </div>
            </div>
            <p className="text-white/90 text-sm leading-relaxed">
              Mumbai's highly respected facility for Bone, Spine, Knee, Neuro, and Pain Care. Over 30,000 treatments performed across 5 Indian states.
            </p>
            <div className="flex space-x-4">
              {(settings.social_links && settings.social_links.length > 0 ? settings.social_links : [
                { platform: 'facebook', url: '#' },
                { platform: 'twitter', url: '#' },
                { platform: 'instagram', url: '#' },
                { platform: 'youtube', url: '#' }
              ]).map((social, i) => {
                // More robust platform name matching
                const platform = (social.platform || '').toLowerCase().trim();
                const iconMap = {
                  'facebook': FiFacebook,
                  'fb': FiFacebook,
                  'instagram': FiInstagram,
                  'ig': FiInstagram,
                  'insta': FiInstagram,
                  'twitter': FiTwitter,
                  'tweet': FiTwitter,
                  'x': FiTwitter,
                  'youtube': FiYoutube,
                  'yt': FiYoutube,
                  'linkedin': FiLinkedin,
                  'linked': FiLinkedin,
                  'whatsapp': FiInstagram, // fallback to instagram icon for now
                };
                const IconComponent = iconMap[platform] || FiInstagram;
                
                return (
                  <a key={`${platform}-${i}`} href={social.url || '#'} className="w-10 h-10 bg-emerald-900/50 rounded-full flex items-center justify-center hover:bg-emerald-500 transition-all duration-300 group">
                    <SafeIcon icon={IconComponent} className="w-5 h-5 text-emerald-300 group-hover:text-white" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Therapy Column */}
          <div className="space-y-6">
            <h3 className="text-lg font-bold border-l-4 border-emerald-500 pl-4 uppercase tracking-wider text-white">Therapies</h3>
            <ul className="space-y-3">
              {therapyLinks.map((link, i) => (
                <li key={i}>
                  <a href="#" className="text-white/90 hover:text-white text-sm flex items-center group transition-colors">
                    <SafeIcon icon={FiArrowRight} className="w-3 h-3 mr-2 opacity-0 group-hover:opacity-100 -ml-5 group-hover:ml-0 transition-all" />
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links Column */}
          <div className="space-y-6">
            <h3 className="text-lg font-bold border-l-4 border-emerald-500 pl-4 uppercase tracking-wider text-white">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link, i) => (
                <li key={i}>
                  <a href={link.href} className="text-white/90 hover:text-white text-sm flex items-center group transition-colors">
                    <SafeIcon icon={FiArrowRight} className="w-3 h-3 mr-2 opacity-0 group-hover:opacity-100 -ml-5 group-hover:ml-0 transition-all" />
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div className="space-y-6">
            <h3 className="text-lg font-bold border-l-4 border-emerald-500 pl-4 uppercase tracking-wider text-white">Get In Touch</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3 group">
                <div className="w-10 h-10 bg-emerald-900/50 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-500 transition-colors">
                  <SafeIcon icon={FiPhone} className="w-5 h-5 text-emerald-300 group-hover:text-white" />
                </div>
                <div>
                  <p className="text-xs text-white/70 font-bold uppercase">Call Us</p>
                  <a href={`tel:${settings.contact_phone || settings.phone1 || '9581556660'}`} className="text-white hover:text-emerald-300 font-medium transition-colors">{settings.contact_phone || settings.phone1 || '9581556660'}</a>
                </div>
              </div>
              <div className="flex items-start space-x-3 group">
                <div className="w-10 h-10 bg-emerald-900/50 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-500 transition-colors">
                  <SafeIcon icon={FiMapPin} className="w-5 h-5 text-emerald-300 group-hover:text-white" />
                </div>
                <div>
                  <p className="text-xs text-white/70 font-bold uppercase">Main Center</p>
                  <p className="text-white/90 text-sm">{settings.company_address || 'Mulund East, Mumbai 400081'}</p>
                </div>
              </div>
              <div className="pt-4 border-t border-white/20">
                <p className="text-[10px] text-white/80 leading-relaxed font-medium">
                  Dr. Saurabh holds licenses for acupuncture, naturopathy, Jiva nadi therapy, psychology, chiropractic, yoga, and trainers.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-10 border-t border-white/20 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <p className="text-white/70 text-xs">
            © {new Date().getFullYear()} JIVA NADI THERAPY & CHIROPRACTOR CENTER. All rights reserved.
          </p>
          <div className="flex space-x-6">
            <a href="/terms" className="text-white/70 hover:text-white text-xs transition-colors">Terms of Service</a>
            <a href="/cookie-policy" className="text-white/70 hover:text-white text-xs transition-colors">Cookie Policy</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;